import { doc, getDoc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';

import { db } from '../firebase.js';
import {
  CommissionRulesSchema,
  MoneyPolicySchema,
  PlanSchema,
  type CommissionRules,
  type MoneyPolicy,
} from '@kilat/shared';

import { createRepo } from './_base.js';

/**
 * `platformConfig/plans/items/{planId}` — operator-facing subscription
 * plans. Public-read per firestore.rules so the pricing-calculator on
 * the marketing site can show live prices; super-admin writes only.
 *
 * Sprint 81 — first super-admin editor surface for plans + their fees.
 */
export const plansRepo = createRepo({
  path: 'platformConfig/plans/items',
  schema: PlanSchema,
  name: 'platformConfig/plans/items',
});

/**
 * `platformConfig/commissionRules` — singleton doc driving both the
 * agent commission engine and the recruiter splits (Sprint 80+). The
 * existing `createRepo` is collection-shaped; for this singleton we
 * roll a thin live-subscription + setter directly.
 */
const COMMISSION_RULES_PATH = 'platformConfig/commissionRules';

export const commissionRulesRepo = {
  async get(): Promise<CommissionRules | null> {
    const snap = await getDoc(doc(db, COMMISSION_RULES_PATH));
    if (!snap.exists()) return null;
    const parsed = CommissionRulesSchema.safeParse(snap.data());
    return parsed.success ? parsed.data : null;
  },
  snapshot(cb: (rules: CommissionRules | null) => void): () => void {
    return onSnapshot(doc(db, COMMISSION_RULES_PATH), (snap) => {
      if (!snap.exists()) return cb(null);
      const parsed = CommissionRulesSchema.safeParse(snap.data());
      cb(parsed.success ? parsed.data : null);
    });
  },
  async set(rules: CommissionRules): Promise<void> {
    const parsed = CommissionRulesSchema.parse(rules);
    await setDoc(doc(db, COMMISSION_RULES_PATH), parsed);
  },
  async update(patch: Partial<CommissionRules>): Promise<void> {
    await updateDoc(doc(db, COMMISSION_RULES_PATH), patch as Record<string, unknown>);
  },
};

/**
 * `platformConfig/moneyPolicy` — the trial length, the toyyibPay fee table and
 * the billing-lifecycle day counts (ADR-0033). Singleton doc, same shape as
 * `commissionRulesRepo` above, and read live by `getMoneyPolicy()` in
 * `apps/functions/src/lib/moneyPolicy.ts`.
 *
 * ⚠ `set()` deliberately does NOT persist the Zod-parsed clone the way
 * `commissionRulesRepo.set()` does. That is safe there only because
 * `CommissionRules` is all numbers and enums; `MoneyPolicy` carries an
 * `updatedAt` **Timestamp**, and a parsed Timestamp is a value Firestore
 * refuses to encode (ADR-0026/ADR-0027 — the same hazard the shared repo base
 * documents). So we validate, throw away the clone, and write the caller's
 * ORIGINAL object.
 */
const MONEY_POLICY_PATH = 'platformConfig/moneyPolicy';

export const moneyPolicyRepo = {
  async get(): Promise<MoneyPolicy | null> {
    const snap = await getDoc(doc(db, MONEY_POLICY_PATH));
    if (!snap.exists()) return null;
    const parsed = MoneyPolicySchema.safeParse(snap.data());
    return parsed.success ? parsed.data : null;
  },
  /**
   * Live subscription. `onError` matters here: the editor has to tell a missing
   * doc ("run the seed") apart from an unreadable one ("you are not a
   * super-admin / you are offline"), and without it both look like `null`.
   */
  snapshot(cb: (policy: MoneyPolicy | null) => void, onError?: (err: Error) => void): () => void {
    return onSnapshot(
      doc(db, MONEY_POLICY_PATH),
      (snap) => {
        if (!snap.exists()) return cb(null);
        const parsed = MoneyPolicySchema.safeParse(snap.data());
        cb(parsed.success ? parsed.data : null);
      },
      (err) => onError?.(err),
    );
  },
  async set(policy: MoneyPolicy): Promise<void> {
    // Validate, then discard the parsed clone — see the Timestamp note above.
    MoneyPolicySchema.parse(policy);
    await setDoc(doc(db, MONEY_POLICY_PATH), policy);
  },
};
